import { styled, SxProps, useTheme } from '@mui/material/styles'
import React, { ReactElement } from 'react'
import { ReactComponent as ArrowIcon } from 'src/assets/svg/arrow.svg'
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material'
import { getThemeColor } from 'src/utils'
import { MUIThemeColors } from 'src/mui-theme'

interface StyledAccordionProps {
  accordionHeight?: string
  accordionPaddingBottom?: string
  removeBoxShadow?: boolean
  borderRadius?: string
  bgColor?: string
  border?: string
  expandedBottomMargin?: string
  expandedTopMargin?: string
}
const customProps: Array<keyof StyledAccordionProps> = [
  'accordionHeight',
  'accordionPaddingBottom',
  'removeBoxShadow',
  'borderRadius',
  'bgColor',
  'border',
  'expandedBottomMargin',
  'expandedTopMargin',
]
const StyledAccordion = styled(Accordion, {
  shouldForwardProp: prop => !customProps.includes(prop as keyof StyledAccordionProps),
})<StyledAccordionProps>(
  ({
    accordionHeight,
    accordionPaddingBottom,
    removeBoxShadow,
    borderRadius,
    bgColor,
    border,
    expandedBottomMargin,
    expandedTopMargin,
  }) => ({
    boxShadow: removeBoxShadow ? 'none' : '',
    '&::before': {
      display: removeBoxShadow ? 'none' : '',
    },
    '&.Mui-expanded': {
      marginBottom: expandedBottomMargin ?? '16px',
      marginTop: expandedTopMargin ?? '16px',
    },
    '& .MuiAccordionSummary-root': {
      height: accordionHeight ?? '',
      minHeight: '32px !important',
      border: border ?? '1px solid #E0E0E0',
      paddingLeft: '8px',
      paddingRight: '8px',
    },
    paddingBottom: accordionPaddingBottom ?? '0px',
    borderRadius: borderRadius,
    backgroundColor: bgColor,
  }),
)
const StyledAccordionSummary = styled(AccordionSummary)(() => ({
  flexDirection: 'row-reverse',
  height: '45px',
  gap: '4px',
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)',
  },
}))

const StyledAccordionDetails = styled(AccordionDetails)({
  padding: 0,
  backgroundColor: 'white',
})

interface Props {
  title: string | ReactElement
  id?: EntityId | number
  iconColor?: MUIThemeColors
  expanded?: boolean
  sx?: SxProps
  onChange?: (event: React.SyntheticEvent, expanded: boolean) => void
  accordionHeight?: string
  accordionPaddingBottom?: string
  removeBoxShadow?: boolean
  borderRadius?: string
  bgColor?: string
  border?: string
  expandedBottomMargin?: string
  expandedIcon?: ReactElement
  expandedTopMargin?: string
  transitionDuration?: number
}
const AccordionCommon: React.FC<Props> = ({
  title,
  children,
  id,
  iconColor,
  expanded,
  sx,
  onChange,
  accordionHeight,
  accordionPaddingBottom,
  removeBoxShadow = false,
  borderRadius,
  bgColor,
  border,
  expandedBottomMargin,
  expandedIcon,
  expandedTopMargin,
  transitionDuration,
}) => {
  const theme = useTheme()
  return (
    <StyledAccordion
      key={id}
      expanded={expanded}
      onChange={onChange}
      accordionHeight={accordionHeight}
      accordionPaddingBottom={accordionPaddingBottom}
      removeBoxShadow={removeBoxShadow}
      borderRadius={borderRadius}
      bgColor={bgColor}
      border={border}
      expandedBottomMargin={expandedBottomMargin}
      expandedTopMargin={expandedTopMargin}
      {...(transitionDuration !== undefined ? { TransitionProps: { timeout: transitionDuration } } : {})}
    >
      <StyledAccordionSummary
        sx={sx}
        expandIcon={
          expandedIcon ? (
            expandedIcon
          ) : (
            <ArrowIcon
              transform="rotate(-90)"
              color={iconColor ? getThemeColor(theme, iconColor) : theme.palette.black[theme.palette.mode]}
            />
          )
        }
        aria-controls="panel1d-content"
        id="panel1d-header"
      >
        {title}
      </StyledAccordionSummary>
      <StyledAccordionDetails>{children}</StyledAccordionDetails>
    </StyledAccordion>
  )
}

export default AccordionCommon
